/**
 * PATH: packages/ui/src/components/settings/HubShortcutsReference.tsx
 * FILE_ID: UI-SETTINGS-HUBSHORTCUTS-001
 * PURPOSE: Unified, per-hub-tabbed keyboard-shortcut reference for the
 *          Settings → Shortcuts page. Replaces the flat 7-item list.
 * WHY: Operators asked to see every hub's shortcuts in one place, organised
 *      into tabs (Email, Kanban, Quote Builder, CRM, QDrive, General) with a
 *      search box, instead of a single generic list. Read-only reference.
 *
 * FLOW:
 *   ┌──────────────────┐   ┌───────────────┐   ┌──────────────────────────┐
 *   │ SHORTCUT_REGISTRY │ → │ selected hub  │ → │ grouped, searchable rows  │
 *   │ + search query    │   │ + filter      │   │ per category              │
 *   └──────────────────┘   └───────────────┘   └──────────────────────────┘
 *
 * DEPENDENCIES:
 *   - ../../shortcuts: registry data + pure filter/group helpers
 *   - ../../lib/utils: cn()
 * RELATED:
 *   - packages/settings-hub/.../SettingsPage.tsx: renders this in the tab
 */

import { useMemo, useState } from 'react'
import { Keyboard, Search } from 'lucide-react'
import { cn } from '../../lib/utils'
import { KeyCap } from './KeyCap'
import {
  getDisplayRegistry,
  DEFAULT_SHORTCUT_HUB,
  filterShortcutSections,
  groupByCategory,
  isShortcutHubId,
  type ShortcutHubId,
} from '../../shortcuts'

export interface HubShortcutsReferenceProps {
  /** Controlled selected hub tab. Falls back to internal state if omitted. */
  selectedHub?: ShortcutHubId
  /** Called when the user picks a different hub tab. */
  onSelectHub?: (hub: ShortcutHubId) => void
  /** Initial hub when uncontrolled. Defaults to 'general'. */
  defaultHub?: ShortcutHubId
  className?: string
}

export function HubShortcutsReference({
  selectedHub,
  onSelectHub,
  defaultHub = DEFAULT_SHORTCUT_HUB,
  className,
}: HubShortcutsReferenceProps) {
  const [internalHub, setInternalHub] = useState<ShortcutHubId>(
    isShortcutHubId(defaultHub) ? defaultHub : DEFAULT_SHORTCUT_HUB,
  )
  const [query, setQuery] = useState('')

  // Data-driven off the action model: the 'general' tab is derived from
  // SHORTCUT_ACTIONS (the same source the dispatcher uses); un-migrated hubs
  // keep their legacy sections. Output is identical to the old registry.
  const registry = useMemo(() => getDisplayRegistry(), [])

  // Controlled when a valid selectedHub is supplied; else internal state.
  const activeHub: ShortcutHubId =
    selectedHub && isShortcutHubId(selectedHub) ? selectedHub : internalHub

  const handleSelect = (hub: ShortcutHubId) => {
    setInternalHub(hub)
    onSelectHub?.(hub)
  }

  // Filtering drives both the visible tabs (hide hubs with zero matches when
  // searching) and the rows in the active tab.
  const filteredSections = useMemo(
    () => filterShortcutSections(registry, query),
    [registry, query],
  )
  const isSearching = query.trim().length > 0

  // When searching, keep the active hub if it still has matches; otherwise
  // fall back to the first hub that does, so the operator always sees results.
  const visibleHubIds = filteredSections.map((s) => s.id)
  const effectiveHub: ShortcutHubId =
    visibleHubIds.includes(activeHub)
      ? activeHub
      : (visibleHubIds[0] ?? activeHub)

  const activeSection = filteredSections.find((s) => s.id === effectiveHub)
  const grouped = useMemo(
    () => (activeSection ? groupByCategory(activeSection.shortcuts) : []),
    [activeSection],
  )

  return (
    <div className={cn('space-y-4', className)}>
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search shortcuts… (try 'view', 'archive', or '⌘')"
          className="w-full h-10 pl-9 pr-3 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          aria-label="Search keyboard shortcuts"
        />
      </div>

      {/* Hub tabs */}
      <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Hubs">
        {registry.map((section) => {
          const hasMatches = !isSearching || visibleHubIds.includes(section.id)
          const isActive = section.id === effectiveHub
          return (
            <button
              key={section.id}
              role="tab"
              type="button"
              aria-selected={isActive}
              disabled={!hasMatches}
              onClick={() => handleSelect(section.id)}
              className={cn(
                'px-3 py-1.5 rounded-full text-[13px] font-medium border transition-colors',
                isActive
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-card text-muted-foreground border-border hover:bg-accent hover:text-foreground',
                !hasMatches && 'opacity-40 cursor-not-allowed hover:bg-card hover:text-muted-foreground',
              )}
            >
              {section.label}
            </button>
          )
        })}
      </div>

      {/* Active hub hint */}
      {activeSection && (
        <p className="text-xs text-muted-foreground">{activeSection.hint}</p>
      )}

      {/* Shortcut rows grouped by category */}
      {activeSection ? (
        <div className="space-y-5">
          {grouped.map(([category, entries]) => (
            <div key={category}>
              <h4 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                {category}
              </h4>
              <div className="divide-y divide-border rounded-lg border border-border overflow-hidden">
                {entries.map((entry, i) => (
                  <div
                    key={`${category}-${i}`}
                    className="flex items-center justify-between gap-3 px-4 py-3 bg-card"
                  >
                    <p className="text-sm text-foreground">{entry.description}</p>
                    <div className="flex items-center gap-1 shrink-0">
                      {entry.keys.map((key, ki) => (
                        <span key={ki} className="flex items-center gap-1">
                          {ki > 0 && (
                            <span className="text-muted-foreground text-xs">+</span>
                          )}
                          <KeyCap>{key}</KeyCap>
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Keyboard className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-sm font-medium text-foreground">No matching shortcuts</p>
          <p className="text-sm text-muted-foreground mt-1">
            Try a different search term.
          </p>
        </div>
      )}
    </div>
  )
}

export default HubShortcutsReference
